import { Doc } from '@dadohr/shared/domainTypes/document';
import {
  ExperienceStatus,
  IExperience
} from '@dadohr/shared/domainTypes/experience';
import { sortBy } from 'lodash';
import {
  getComputedArchiveDate,
  isInstanceArchived
} from '../../../../services/experiences/instance';
import { getTemplateExpectedEndThreshold } from '../../../../services/experiences/template';
import { Timestamp, tsIsAfter } from '../../../../services/time';

export type ProfileExperienceGroup = 'active' | 'upcoming' | 'archived';

export interface ProfileExperienceGroups {
  active: Doc<IExperience>[];
  upcoming: Doc<IExperience>[];
  archived: Doc<IExperience>[];
}

// Suggested and Aborted are legacy statuses that nothing creates any more;
// they are hidden from the profile rather than placed in a group.
const HIDDEN_STATUSES: ExperienceStatus[] = [
  ExperienceStatus.SUGGESTED,
  ExperienceStatus.ABORTED
];

// `launchAt` is the real start (the later of enroll time and the virtual
// `startsAt`); it is null on legacy/partial docs, where `startsAt` is the
// best available start.
export const getProfileExperienceStartDate = (d: IExperience): Timestamp =>
  d.launchAt ?? d.startsAt;

// A manually archived instance shows the date it was archived; otherwise the
// computed auto-archive date (max(expectedEnd, createdAt) + threshold).
export const getProfileExperienceArchiveDate = (
  d: IExperience,
  expectedEndThresholdMs: number
): Timestamp =>
  d.manuallyArchivedAt ?? getComputedArchiveDate(d, expectedEndThresholdMs);

export const getProfileExperienceGroup = (
  d: IExperience,
  expectedEndThresholdMs: number,
  n: Timestamp
): ProfileExperienceGroup => {
  if (isInstanceArchived(d, expectedEndThresholdMs, n)) {
    return 'archived';
  }
  return tsIsAfter(getProfileExperienceStartDate(d), n) ? 'upcoming' : 'active';
};

/**
 * Splits a user's instances into the profile page's three sections. Instances
 * whose template is unknown (`getTemplate` returns undefined) are dropped, as
 * are legacy Suggested / Aborted instances.
 *
 * Sort order: Active and Archived newest first (by start date / archive date);
 * Upcoming soonest first.
 */
export const groupProfileExperiences = (
  instances: Doc<IExperience>[],
  getTemplate: (
    templateId: string
  ) => { expectedEndThreshold?: number } | undefined,
  n: Timestamp
): ProfileExperienceGroups => {
  const groups: ProfileExperienceGroups = {
    active: [],
    upcoming: [],
    archived: []
  };
  const archiveDateById: { [id: string]: number } = {};

  instances.forEach((d) => {
    if (HIDDEN_STATUSES.includes(d.data.status)) {
      return;
    }
    const tpl = getTemplate(d.data.templateId);
    if (!tpl) {
      return;
    }
    const threshold = getTemplateExpectedEndThreshold(tpl);
    const group = getProfileExperienceGroup(d.data, threshold, n);
    if (group === 'archived') {
      archiveDateById[d.id] = getProfileExperienceArchiveDate(
        d.data,
        threshold
      ).toMillis();
    }
    groups[group].push(d);
  });

  const startMillis = (d: Doc<IExperience>) =>
    getProfileExperienceStartDate(d.data).toMillis();

  return {
    active: sortBy(groups.active, (d) => -startMillis(d)),
    upcoming: sortBy(groups.upcoming, startMillis),
    archived: sortBy(groups.archived, (d) => -archiveDateById[d.id])
  };
};
